import {
  useState,
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from 'react';
import { useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { getAuthToken } from '../../utils/auth';
import { contactSyncErrorSummary } from '../../utils/contactSyncError';
import {
  resolveChannelConsent,
  isPocketflowSuppressed,
  isPocketflowSmsScoped,
} from '../../utils/channelConsent';
import {
  setContactSubscription,
  setContactSubscriptionAdminVerifiedStart,
  isMarketingBackendEnabled,
} from '../../api/marketing';
import { AdminVerifiedStartConfirmDialog } from './AdminVerifiedStartConfirmDialog';
import { isMarketingTierEnabledNow } from '../../hooks/useMarketingTierEnabled';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressAutofill } from '@mapbox/search-js-react';
import { getCountryOptions, normalizeCountry } from '@charitystack/shared';
import parsePhoneNumber, {
  parsePhoneNumberFromString,
  isPossiblePhoneNumber,
} from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  InformationCircleIcon,
  UserIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { EmptyState } from '@/components/ui/empty-state';
import { FormSelect } from '../../components/ui/form-select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import { DASHBOARD_PLG_EVENTS, trackDashboardPlg } from '../../utils/dashboardPlgAnalytics';
import {
  CONTACT_CHANNEL_REQUIRED_ERROR,
  CONTACT_CHANNEL_REQUIRED_MESSAGE,
  violatesContactChannelRule,
} from './contactChannelRule';
import {
  EntityIdentityHeader,
  EditorFieldGroup,
  HeaderCloseAction,
  HEADER_ACTION_BUTTON_CLASS,
  RecordDetailsSection,
} from '../../../components/workspaces/EntityWorkspacePrimitives';

// Utility function for classNames
function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface ContactEditorSectionProps {
  workspace: boolean;
  title: string;
  description: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  className?: string;
  drawerTitle?: string;
  showDrawerHeader?: boolean;
  children: ReactNode;
}

function ContactEditorSection({
  workspace,
  title,
  description,
  icon,
  className,
  drawerTitle,
  showDrawerHeader = true,
  children,
}: ContactEditorSectionProps) {
  if (workspace) {
    return (
      <div className={className}>
        <EditorFieldGroup enabled title={title} description={description} icon={icon}>
          {children}
        </EditorFieldGroup>
      </div>
    );
  }

  return (
    <div className={className}>
      {showDrawerHeader && <SectionHeader title={drawerTitle ?? title} />}
      {children}
    </div>
  );
}

interface ContactEditorSubheadingProps {
  workspace: boolean;
  title: string;
  children?: ReactNode;
}

function ContactEditorSubheading({ workspace, title, children }: ContactEditorSubheadingProps) {
  if (!workspace) {
    return <SectionHeader title={title}>{children}</SectionHeader>;
  }

  return (
    <div className="flex min-h-6 items-center gap-1">
      <h3 className="text-sm font-medium text-ink-secondary">{title}</h3>
      {children}
    </div>
  );
}

interface Email {
  value: string;
  is_primary: boolean;
  deleted?: boolean;
  new?: boolean;
  // Post-Phase-5 shape from the backend. `email` is the raw-string field on
  // incoming contact.emails[] entries; we normalize it into `value` for the
  // form state and submit back as `email` via the submit payload mapping.
  email?: string;
}

interface Phone {
  value: string;
  is_primary: boolean;
  deleted?: boolean;
  new?: boolean;
}

interface Address {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postal: string;
  country: string;
  is_primary: boolean;
  deleted?: boolean;
  new?: boolean;
}

interface Contact {
  id?: string;
  firstName: string;
  lastName: string;
  organizationName?: string;
  emails: Email[];
  phones: Phone[];
  addresses: Address[];
  // Per-contact User Login Widget toggles. Both default to true when undefined
  // — the widget treats missing as "allowed" so legacy contacts keep working.
  // Effective value is computed merchant-side: merchant_value AND contact_value.
  allowProfileEdit?: boolean;
  showTaxDocuments?: boolean;
  // Global marketing communication consent (legacy master boolean). Defaults to
  // true when undefined (legacy contacts are treated as opted-in). Persisted on
  // the contactsDB record by contact_upsert; the marketing drain syncs it to
  // PocketFlows.
  communicationConsent?: boolean;
  // Per-channel-consent Rev 4 (U10): per-channel status enums (∈ SUBSCRIBED /
  // UNSUBSCRIBED / NEVER_SUBSCRIBED / UNKNOWN), carried on the contact row from
  // OpenSearch. The drawer edits them as two per-channel toggles (D20-revised:
  // no master switch); the contacts editor accepts either channel's enum on
  // update/create.
  emailConsent?: string;
  smsConsent?: string;
  // Option B (communication-consent Finding 2): derived read-only flag from
  // /validate/contact — true when the contact carries a NARROW per-object
  // PocketFlows opt-out (pf_unsub_campaigns / pf_unsub_triggers). Post-PE-1845
  // such a contact reads as fully SUBSCRIBED at the channel level, so the drawer
  // surfaces a notice that re-enabling a channel does NOT re-subscribe those
  // specific broadcasts. Absent on legacy payloads → treated as false.
  hasBroadcastOptOut?: boolean;
  // PE-1870: raw PocketFlows suppression-scope sentinel, passed through
  // unfiltered by GET /validate/contact. 'sms' means a genuine carrier-level
  // STOP (isPocketflowSmsScoped) -- distinct from a routine admin toggle-off,
  // which never writes this field (see channel-consent.ts:336-355).
  pocketflow_unsubscribed?: string;
}

interface CreateEditContactDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  contact?: Contact;
  onContactSaved?: (contact: Contact) => void;
  merchant?: any;
  presentation?: 'drawer' | 'workspace';
  onWorkspaceBack?: () => void;
  setWorkspaceActions?: (actions: WorkspaceHeaderAction[] | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

interface WorkspaceHeaderAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'ghost' | 'outline' | 'neutral' | 'primary' | 'danger';
  className?: string;
}

// Tab configuration for navigation
const tabs = [
  { id: 'basic_info', name: 'Basic Information', current: true },
  { id: 'contact_details', name: 'Email & Phone', current: false },
  { id: 'address', name: 'Address', current: false },
  { id: 'settings', name: 'Settings', current: false },
];

export default function CreateEditContactDrawer({
  open,
  setOpen,
  contact,
  onContactSaved,
  merchant,
  presentation,
  setWorkspaceActions,
  onDirtyChange,
}: CreateEditContactDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidFields, setInvalidFields] = useState(new Set<string>());
  // PE-1870: the SMS carrier-STOP override confirm dialog + its own pending
  // state, independent of the drawer's main isSubmitting (this is a separate
  // attestation action, not part of the Save flow). `smsCarrierLockCleared`
  // is local-only UI state: once the override succeeds, it forces the locked
  // view off for the rest of this drawer session WITHOUT mutating the
  // `contact` prop (props are treated as immutable here; a parent refetch on
  // next open will reflect the real persisted state).
  const [adminVerifiedStartDialogOpen, setAdminVerifiedStartDialogOpen] = useState(false);
  const [adminVerifiedStartPending, setAdminVerifiedStartPending] = useState(false);
  const [smsCarrierLockCleared, setSmsCarrierLockCleared] = useState(false);
  // When the backend rejects a submit with email_taken, we know which
  // existing contact already owns that email. Stash it by email-row
  // index so the inline error can render a "View contact" link that
  // navigates to /contacts?contact={existingContactID} — the contacts
  // page already knows how to expand a single contact from that param
  // (Donors.js → getContactFilterFromURL).
  const [emailTakenContactIDs, setEmailTakenContactIDs] = useState<Record<number, string>>({});
  // CON-491: the contact rule (an email or a phone; the name is optional). Set
  // when a save is refused for it — by the drawer's own check or by the
  // backend's 400 `contact_channel_required` — and shown inline under Contact
  // methods. Kept out of `invalidFields` on purpose: that set disables Save,
  // and this error clears itself as soon as the email, phone or name changes.
  const [channelError, setChannelError] = useState<string | null>(null);
  const history = useHistory();
  const posthog = usePostHog();
  const initialFormSnapshotRef = useRef('');
  const skipNextDirtyCheckRef = useRef(false);

  // Tab state management. The donor-portal Settings tab is always shown now —
  // the donor_portal_settings_tab feature flag was removed (feature permanent).
  const [activeTab, setActiveTab] = useState<
    'basic_info' | 'contact_details' | 'address' | 'settings'
  >('basic_info');
  const visibleTabs = tabs;

  // Determine if we're in edit mode
  const isEditMode = !!contact;

  const [formData, setFormData] = useState<Contact>({
    firstName: '',
    lastName: '',
    organizationName: '',
    emails: [],
    phones: [],
    addresses: [],
    allowProfileEdit: true,
    showTaxDocuments: true,
    communicationConsent: true,
    // Per-channel-consent Rev 4 creation defaults (match contact_creator):
    // email opted in, SMS never subscribed (opt-in only).
    emailConsent: 'SUBSCRIBED',
    smsConsent: 'NEVER_SUBSCRIBED',
  });

  // Countries list for address autocomplete (frozen canonical options, PE-1878)
  const countries = useMemo(() => getCountryOptions(), []);
  // PE-1878 FE window: a stored country value ("Israel"/"Palestine, State of")
  // may no longer match a canonical option before the data backfill runs. Keep
  // any orphan value selectable so an existing row doesn't silently go blank
  // (and a re-save doesn't drop the country). Mirrors ContactPortalProfileTab.
  const countryLabels = useMemo(
    () => new Set(countries.map((c: { value: string; label: string }) => c.label)),
    [countries],
  );

  // Pre-populate form data when in edit mode
  useEffect(() => {
    // PE-1870: reset the local override-cleared flag whenever the drawer
    // (re)opens, so it never leaks the previous contact's cleared state onto
    // a different (still-locked) contact.
    setSmsCarrierLockCleared(false);
    if (isEditMode && contact) {
      // In edit mode, include all items (including soft-deleted ones) but ensure proper primary assignment
      // IMPORTANT: Sanitize null values from DynamoDB to prevent .trim() errors
      // Post-Phase-5 contact.emails[] entries use `email` as the raw-string
      // field. The form state keeps `value` as its internal input-binding key,
      // so we map .email -> .value on load and the reverse on submit.
      const allEmails = (contact.emails || []).map((email) => ({
        ...email,
        value: email.email || '',
      }));
      const allPhones = (contact.phones || []).map((phone) => ({
        ...phone,
        value: phone.value || '',
      }));
      const allAddresses = (contact.addresses || []).map((address) => ({
        ...address,
        line1: address.line1 || '',
        line2: address.line2 || '',
        city: address.city || '',
        region: address.region || '',
        postal: address.postal || '',
        country: address.country || '',
      }));

      // Filter active items for primary assignment logic
      const activeEmails = allEmails.filter((email) => !email.deleted);
      const activePhones = allPhones.filter((phone) => !phone.deleted);
      const activeAddresses = allAddresses.filter((address) => !address.deleted);

      // Ensure at least one primary item exists for each type among active items
      if (activeEmails.length > 0 && !activeEmails.some((email) => email.is_primary)) {
        activeEmails[0].is_primary = true;
      }
      if (activePhones.length > 0 && !activePhones.some((phone) => phone.is_primary)) {
        activePhones[0].is_primary = true;
      }
      if (activeAddresses.length > 0 && !activeAddresses.some((address) => address.is_primary)) {
        activeAddresses[0].is_primary = true;
      }

      const nextFormData = {
        id: contact.id,
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        organizationName: contact.organizationName || '',
        emails: allEmails, // Include all emails (including soft-deleted) with sanitized values
        phones: allPhones, // Include all phones (including soft-deleted) with sanitized values
        addresses: allAddresses, // Include all addresses (including soft-deleted) with sanitized values
        // Toggles: missing => true (legacy contacts had no field; default-allow).
        allowProfileEdit: contact.allowProfileEdit !== false,
        showTaxDocuments: contact.showTaxDocuments !== false,
        // Seed master consistently with the derived channel view: a PF-suppressed
        // contact derives master=false, so seeding it `true` here (via the raw
        // `!== false`) would leave the form internally inconsistent (master ON,
        // both channels OFF). Save currently sends communicationConsent=null
        // (no-change), so this is form-state hygiene today — but it keeps the seed
        // honest if a master control is ever re-surfaced (D20 removed it).
        communicationConsent: isPocketflowSuppressed(contact)
          ? false
          : contact.communicationConsent !== false,
        // Per-channel-consent Rev 4: seed each channel's toggle from the SHARED
        // derivation (`utils/channelConsent`), the ONE dashboard mirror of the
        // Python source-of-truth `derive_channel_consent_enums`. This is the
        // SAME function the contacts-table column uses, so the drawer and the
        // table can never disagree (INV-21). CRITICAL: it honors the
        // `pocketflow_unsubscribed` read-floor — the previous inline fallback
        // mapped `communicationConsent !== false → SUBSCRIBED` and NEVER checked
        // PF suppression, so a PF-suppressed legacy contact (raw item from
        // GET /validate/contact, enum-less) rendered as Subscribed in the drawer
        // while the table read Unsubscribed, and saving would auto-flip master
        // (D19) and LIFT a real suppression the donor never rescinded.
        emailConsent: resolveChannelConsent(contact, 'email'),
        smsConsent: resolveChannelConsent(contact, 'sms'),
      };
      initialFormSnapshotRef.current = JSON.stringify(nextFormData);
      skipNextDirtyCheckRef.current = true;
      setFormData(nextFormData);
      onDirtyChange?.(false);
    } else {
      // Reset form for create mode
      const nextFormData = {
        firstName: '',
        lastName: '',
        organizationName: '',
        emails: [],
        phones: [],
        addresses: [],
        allowProfileEdit: true,
        showTaxDocuments: true,
        communicationConsent: true,
        emailConsent: 'SUBSCRIBED',
        smsConsent: 'NEVER_SUBSCRIBED',
      };
      initialFormSnapshotRef.current = JSON.stringify(nextFormData);
      skipNextDirtyCheckRef.current = true;
      setFormData(nextFormData);
      onDirtyChange?.(false);
    }
  }, [isEditMode, contact, onDirtyChange, open]);

  useEffect(() => {
    if (!open || !initialFormSnapshotRef.current) return;
    if (skipNextDirtyCheckRef.current) {
      skipNextDirtyCheckRef.current = false;
      return;
    }
    onDirtyChange?.(JSON.stringify(formData) !== initialFormSnapshotRef.current);
  }, [formData, onDirtyChange, open]);

  // Real-time validation when form data changes
  useEffect(() => {
    if (open) {
      const duplicateFields = checkForDuplicates();
      setInvalidFields((prev) => {
        const newInvalidFields = new Set(prev);
        // Remove old duplicate errors
        Array.from(prev).forEach((field) => {
          if (field.includes('-duplicate-') || field.includes('-invalid-')) {
            newInvalidFields.delete(field);
          }
        });
        // Add new duplicate errors
        duplicateFields.forEach((field) => newInvalidFields.add(field));
        return newInvalidFields;
      });
    }
  }, [formData.emails, formData.phones, formData.addresses, open]);

  // CON-491: any change to the fields the contact rule reads clears its error.
  useEffect(() => {
    setChannelError(null);
  }, [formData.emails, formData.phones, formData.firstName, formData.lastName, open]);

  // Helper function to check if a specific field is invalid
  const isFieldInvalid = (fieldId: string) => {
    return invalidFields.has(fieldId);
  };

  // Handle field focus to clear validation errors
  const handleFieldFocus = (fieldName: string) => {
    // Only clear invalid format errors on focus, not duplicate errors
    // Duplicate errors should only be cleared when the duplication is actually resolved
    if (fieldName.includes('-invalid-')) {
      setInvalidFields((prev) => {
        const newInvalidFields = new Set(prev);
        newInvalidFields.delete(fieldName);
        return newInvalidFields;
      });
    }
  };

  // Validation helper functions
  const validateEmail = (email: string): boolean => {
    if (!email?.trim()) return true; // Empty emails are valid (optional)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone?.trim()) return true; // Empty phones are valid (optional)

    // Normalize input: remove spaces, dashes, parentheses, dots
    const cleanPhone = phone.replace(/[\s\-().]/g, '');
    const phoneToValidate = cleanPhone.startsWith('+') ? cleanPhone : '+' + cleanPhone;

    try {
      const phoneNumber = parsePhoneNumberFromString(phoneToValidate);

      if (!phoneNumber) return false;

      // Check both: valid structure and possible number
      return phoneNumber.isValid() && isPossiblePhoneNumber(phoneToValidate);
    } catch {
      return false;
    }
  };

  const formatPhoneForPayload = (phone: string): string => {
    if (!phone?.trim()) return phone;

    // Clean the phone number first - remove spaces, dashes, parentheses, dots
    const cleanPhone = phone.replace(/[\s\-().]/g, '');

    try {
      // For numbers that don't start with +, add it since react-phone-input-2 removes it
      const phoneToFormat = cleanPhone.startsWith('+') ? cleanPhone : '+' + cleanPhone;

      // Parse and format the phone number using libphonenumber-js
      const phoneNumber = parsePhoneNumber(phoneToFormat);

      if (phoneNumber && phoneNumber.isValid()) {
        // Return in E.164 format (international standard): +34613628904
        return phoneNumber.format('E.164');
      }

      // If not valid, return the original cleaned phone with + prefix
      return phoneToFormat;
    } catch (error) {
      console.error('formatPhoneForPayload error:', error);
      // Fallback: ensure phone has + prefix
      return cleanPhone.startsWith('+') ? cleanPhone : '+' + cleanPhone;
    }
  };

  const checkForDuplicates = () => {
    const newInvalidFields = new Set<string>();

    // Track seen emails to mark only subsequent duplicates
    const seenEmails = new Set<string>();
    formData.emails.forEach((email, index) => {
      if (!email.deleted && (email.value || '').trim()) {
        const emailValue = (email.value || '').toLowerCase().trim();
        if (seenEmails.has(emailValue)) {
          newInvalidFields.add(`email-duplicate-${index}`);
        } else {
          seenEmails.add(emailValue);
        }
      }
    });

    // Track seen phones to mark only subsequent duplicates
    const seenPhones = new Set<string>();
    formData.phones.forEach((phone, index) => {
      if (!phone.deleted && (phone.value || '').trim()) {
        const phoneValue = (phone.value || '').replace(/\D/g, '');
        if (seenPhones.has(phoneValue)) {
          newInvalidFields.add(`phone-duplicate-${index}`);
        } else {
          seenPhones.add(phoneValue);
        }
      }
    });

    // Check for duplicate addresses
    // Track seen addresses to mark only subsequent duplicates
    const seenAddresses = new Set<string>();
    formData.addresses.forEach((address, index) => {
      if (
        !address.deleted &&
        ((address.line1 || '').trim() ||
          (address.line2 || '').trim() ||
          (address.city || '').trim() ||
          (address.region || '').trim() ||
          (address.postal || '').trim())
      ) {
        const addressKey = `${(address.line1 || '').trim()}-${(address.line2 || '').trim()}-${(
          address.city || ''
        ).trim()}-${(address.region || '').trim()}-${(address.postal || '').trim()}-${(
          address.country || ''
        ).trim()}`.toLowerCase();
        if (seenAddresses.has(addressKey)) {
          newInvalidFields.add(`address-duplicate-${index}`);
        } else {
          seenAddresses.add(addressKey);
        }
      }
    });

    return newInvalidFields;
  };

  // Validate form fields. Returns the invalid field ids (empty when valid) so
  // the caller reads this run's result, not the previous render's state.
  // First and last name are optional (CON-491); the contact rule is checked
  // separately in handleSubmit.
  const validateForm = (): Set<string> => {
    const newInvalidFields = new Set<string>();

    // Validate all emails
    formData.emails.forEach((email, index) => {
      if (!email.deleted && (email.value || '').trim()) {
        if (!validateEmail(email.value)) {
          newInvalidFields.add(`email-invalid-${index}`);
        }
      }
    });

    // Validate all phones
    formData.phones.forEach((phone, index) => {
      if (!phone.deleted && (phone.value || '').trim()) {
        if (!validatePhone(phone.value)) {
          newInvalidFields.add(`phone-invalid-${index}`);
        }
      }
    });

    // Check for duplicates
    const duplicateFields = checkForDuplicates();
    duplicateFields.forEach((field) => newInvalidFields.add(field));

    setInvalidFields(newInvalidFields);
    return newInvalidFields;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isSubmitting) return;

    const submitInvalidFields = validateForm();
    if (submitInvalidFields.size > 0) {
      const errorMessages = [];

      // Check for email validation errors
      const emailInvalidErrors = Array.from(submitInvalidFields).filter((field) =>
        field.startsWith('email-invalid-'),
      );
      if (emailInvalidErrors.length > 0) {
        errorMessages.push('Please enter valid email addresses');
      }

      // Check for phone validation errors
      const phoneInvalidErrors = Array.from(submitInvalidFields).filter((field) =>
        field.startsWith('phone-invalid-'),
      );
      if (phoneInvalidErrors.length > 0) {
        errorMessages.push('Please enter valid phone numbers');
      }

      // Check for duplicate errors
      const duplicateErrors = Array.from(submitInvalidFields).filter((field) =>
        field.includes('-duplicate-'),
      );
      if (duplicateErrors.length > 0) {
        errorMessages.push('Please remove duplicate entries');
      }

      const errorMessage =
        errorMessages.length > 0
          ? errorMessages.join('. ') + '.'
          : 'Please fill in all required fields correctly.';

      toast.error(errorMessage);
      return;
    }

    // CON-491: an email or a phone is required; the name is optional. Same
    // answer as contacts-db-backend: a create needs a channel, an edit may not
    // remove the last one, and a name-only contact that never had one still
    // saves while it keeps its name.
    if (
      violatesContactChannelRule({
        isEditMode,
        form: formData,
        original: contact,
        isUsableEmail: validateEmail,
        isUsablePhone: validatePhone,
      })
    ) {
      setChannelError(CONTACT_CHANNEL_REQUIRED_MESSAGE);
      setActiveTab('contact_details');
      toast.error(CONTACT_CHANNEL_REQUIRED_MESSAGE);
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = `${import.meta.env.VITE_APP_DASHBOARD_CONTACTS_URL}/contacts/${
        isEditMode ? 'update' : 'create'
      }`;

      const method = isEditMode ? 'PUT' : 'POST';

      // Clean up formData to remove empty objects and ensure arrays have at least one item with is_primary: true
      const cleanEmails = formData.emails.filter((email) => (email.value || '').trim() !== '');
      const cleanPhones = formData.phones.filter((phone) => (phone.value || '').trim() !== '');
      const cleanAddresses = formData.addresses.filter(
        (address) =>
          (address.line1 || '').trim() !== '' ||
          (address.line2 || '').trim() !== '' ||
          (address.city || '').trim() !== '' ||
          (address.region || '').trim() !== '' ||
          (address.postal || '').trim() !== '',
      );

      // Format phone numbers for payload
      // This converts various formats (0034613628904, +34613628904, etc.) to E.164 format (+34613628904)
      const formattedPhones = cleanPhones.map((phone) => ({
        ...phone,
        value: formatPhoneForPayload(phone.value),
      }));

      // For edit mode, include all original items (including soft-deleted ones) and merge with current form data
      let finalEmails = cleanEmails;
      let finalPhones = formattedPhones;
      let finalAddresses = cleanAddresses;

      if (isEditMode && contact) {
        // Merge with original data, preserving soft-deleted items
        const originalEmails = contact.emails || [];
        const originalPhones = contact.phones || [];
        const originalAddresses = contact.addresses || [];

        // For emails: keep original soft-deleted ones that haven't been restored
        const restoredEmailValues = new Set(cleanEmails.map((email) => email.value));
        finalEmails = [
          ...originalEmails.filter(
            (email) => email.deleted && !restoredEmailValues.has(email.value),
          ), // Keep only soft-deleted that weren't restored
          ...cleanEmails, // Add current active ones (including restored ones)
        ];

        // Same logic for phones
        const restoredPhoneValues = new Set(
          cleanPhones.map((phone) => phone.value), // Use original values for comparison
        );
        finalPhones = [
          ...originalPhones.filter(
            (phone) => phone.deleted && !restoredPhoneValues.has(phone.value),
          ), // Keep only soft-deleted that weren't restored
          ...formattedPhones, // Add current active ones (including restored ones) - these are formatted
        ];

        // Same logic for addresses
        const restoredAddressKeys = new Set(
          cleanAddresses.map((addr) => addr.line1 + addr.city + addr.region),
        );
        finalAddresses = [
          ...originalAddresses.filter(
            (address) =>
              address.deleted &&
              !restoredAddressKeys.has(address.line1 + address.city + address.region),
          ), // Keep only soft-deleted that weren't restored
          ...cleanAddresses, // Add current active ones (including restored ones)
        ];
      }

      // Per-channel-consent Rev 4: the drawer has no master switch (D20-revised);
      // it drives consent through the two per-channel enums (emailConsent /
      // smsConsent). It MUST NOT derive-and-write the master boolean — Rev-4's
      // load-bearing invariant is "master is written explicitly, NEVER derived
      // from the channels" (parity fixture + §1.1). Sending a derived master
      // would also violate D16 (a channel toggled off must never flip master
      // off) and could trip contact_editor's master-off hard-suppress cascade.
      // Instead send communicationConsent=null ("no change" — contact_editor
      // test (g)/(g2)): the editor writes ONLY the explicit channel enums and
      // leaves master exactly as stored. The dedicated consent paths (checkout
      // re-grant, portal/unsub-page master-off, webhooks) own the master axis.
      const refactoredFormData = {
        ...formData,
        communicationConsent: null,
        emails:
          finalEmails.length === 0
            ? [{ email: '', is_primary: true }]
            : finalEmails.map((e) => {
                // Translate internal form-state {value, is_primary, ...} to the
                // backend payload shape {email, is_primary, ...}. The form
                // state keeps `value` as its input-binding key for convenience;
                // the backend (contact_editor / contact_creator) reads `email`.
                const { new: _new, value, ...rest } = e;
                return {
                  ...rest,
                  email: value || e.email || '',
                  is_primary: Boolean(e.is_primary),
                };
              }),
        phones:
          finalPhones.length === 0
            ? [{ value: '', is_primary: true }]
            : finalPhones.map((phone) => {
                // Remove the 'new' flag from phones
                const { new: _, ...phoneWithoutNew } = phone;
                return phoneWithoutNew;
              }),
        addresses:
          finalAddresses.length === 0
            ? [
                {
                  line1: '',
                  line2: '',
                  city: '',
                  region: '',
                  postal: '',
                  country: '',
                  is_primary: true,
                },
              ]
            : finalAddresses.map((address) => {
                // Remove the 'new' flag from addresses
                const { new: _, ...addressWithoutNew } = address;
                return addressWithoutNew;
              }),
      };

      const response = await fetch(endpoint, {
        method,
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify({
          ...(isEditMode
            ? { ...refactoredFormData, contact_id: refactoredFormData.id }
            : refactoredFormData),
          base_merchant: merchant?.merchantName || '',
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        // CON-491: the backend refused the save under the contact rule (no
        // email and no phone). Show its message inline under Contact methods,
        // where staff fix it.
        if (response.status === 400 && responseData?.error === CONTACT_CHANNEL_REQUIRED_ERROR) {
          const message = responseData.message || CONTACT_CHANNEL_REQUIRED_MESSAGE;
          setChannelError(message);
          setActiveTab('contact_details');
          toast.error(message);
          return;
        }
        // email_taken: per-merchant uniqueness collision. Backend (both
        // /contacts/update and /contacts/create) returns the offending
        // emailMerchantKey and the contact ID that already owns it.
        // Translate that into an inline error under the matching email
        // input instead of a generic toast — the toast loses context
        // for users editing multiple emails at once.
        if (responseData?.error === 'email_taken' && responseData?.emailMerchantKey) {
          const merchantName = merchant?.merchantName || '';
          // emailMerchantKey shape is `${lowercased_email}-${merchantName}`
          // (build_email_merchant_key in contact_key_utils.py). Strip the
          // exact suffix to recover the email — safer than splitting on
          // '-' because both halves can legally contain dashes.
          const suffix = merchantName ? `-${merchantName}` : '';
          const takenEmailLower =
            suffix && responseData.emailMerchantKey.endsWith(suffix)
              ? responseData.emailMerchantKey.slice(0, -suffix.length)
              : '';
          const takenIndex = takenEmailLower
            ? formData.emails.findIndex(
                (e) => !e.deleted && (e.value || '').toLowerCase().trim() === takenEmailLower,
              )
            : -1;
          if (takenIndex >= 0) {
            setInvalidFields((prev) => {
              const next = new Set(prev);
              next.add(`email-taken-${takenIndex}`);
              return next;
            });
            // Remember which contact already owns this email so the
            // inline error can offer "View contact". Falsy/missing ID
            // is OK — the UI just renders the message without the link.
            if (responseData.existingContactID) {
              setEmailTakenContactIDs((prev) => ({
                ...prev,
                [takenIndex]: responseData.existingContactID as string,
              }));
            }
            // Switch to the Contact Details tab if we're not on it so
            // the user can actually see the error they need to fix.
            setActiveTab('contact_details');
            return;
          }
          // Fell through (couldn't map back to a field) — surface the
          // backend message so the user still understands what happened.
          toast.error(responseData.message || 'This email is already in use.');
          return;
        }
        throw new Error(responseData.message || 'Failed to save contact');
      }

      // ── Per-channel consent re-grant via the marketing path ──────────────────
      //
      // The contacts-db PUT above writes the raw emailConsent/smsConsent enums,
      // but it does NOT clear a PocketFlows suppression scope
      // (`pocketflow_unsubscribed`) or issue a PF resubscribe. So for a contact
      // under an active PF opt-out (e.g. a one-click email List-Unsubscribe →
      // scope='future'), the read-floor re-applies on reopen and the toggle
      // appears not to save (the "toggle won't stick" bug on 9c2e9823). Only the
      // marketing `setContactSubscription` use case clears the scope AND
      // resubscribes at PF.
      //
      // Hybrid (Akif 2026-07-21): attempt the marketing path when the marketing
      // UI is enabled (flag + view permission). That gate is a fast-path only —
      // it is NOT authoritative about whether THIS org's marketingTierStatus is
      // writable (the flag is global; a NOT_ENABLED/READ_ONLY org can have it
      // on). The backend `assertMarketingWritable` is the real gate and returns
      // FORBIDDEN for a non-writable org. We treat that FORBIDDEN as a benign
      // skip — for such an org the contacts-db enum write above IS the durable
      // consent (there is no PF customer to resubscribe), so consent already
      // saved. Only a NON-FORBIDDEN failure is a real error. This removes the
      // dependency on the FE gate matching the server gate (adversarial-review
      // Finding 1: a NOT_ENABLED org + flag-on otherwise looped on a permanent
      // error toast over an already-successful save).
      // Also gate on isMarketingBackendEnabled(): isMarketingTierEnabledNow() is
      // only the flag + view-permission, so in an env where the flag is on but
      // VITE_APP_MARKETING_BACKEND_TRPC_URL is unset, getMarketingClient() throws
      // a plain Error (no data.code) → the benign-FORBIDDEN discriminator misses
      // it → permanent error-toast loop over an already-successful save (Greptile
      // #2213, the same class as the tier-status gap). Skipping the marketing
      // call when the backend is unconfigured is safe: the contacts-db PUT above
      // already persisted the enum and there's no client to resubscribe with.
      if (isEditMode && contact?.id && isMarketingTierEnabledNow() && isMarketingBackendEnabled()) {
        // Capture the narrowed id so the async closure below keeps `string`
        // (TS widens `contact.id` back to `string | undefined` inside a nested fn).
        const contactId = contact.id;
        // Compare against the ORIGINAL derived (floored) view the toggles were
        // seeded from — the same resolveChannelConsent the table uses (INV-21).
        const origEmail = resolveChannelConsent(contact, 'email') === 'SUBSCRIBED';
        const origSms = resolveChannelConsent(contact, 'sms') === 'SUBSCRIBED';
        const nextEmail = formData.emailConsent === 'SUBSCRIBED';
        const nextSms = formData.smsConsent === 'SUBSCRIBED';
        const emailChanged = origEmail !== nextEmail;
        // PE-1963 follow-up: the verified-START override writes the SMS grant
        // server-side with its own `admin_verified_start` provenance — a
        // distinct, auditable consent source (see AdminVerifiedStartConfirmDialog)
        // that 10DLC compliance depends on.
        //
        // `origSms` comes from the UNREFRESHED `contact` prop, so after an
        // override it still reports the pre-override value and is no longer the
        // server's actual state. Diffing against it is wrong in BOTH directions:
        //
        //   - re-grant: orig=false, next=true → looks changed → the save would
        //     rewrite the same grant with ordinary dashboard provenance,
        //     silently destroying the stronger audit source.
        //   - revoke:   orig=false, next=false → looks unchanged → the save
        //     would skip the write and leave the contact SUBSCRIBED against the
        //     admin's explicit decision.
        //
        // Comparing against the EFFECTIVE server state fixes both: after an
        // override the contact is subscribed, so a still-on toggle is a genuine
        // no-op and a turned-off toggle is a genuine revoke.
        const effectiveOrigSms = smsCarrierLockCleared ? true : origSms;
        const smsChanged = effectiveOrigSms !== nextSms;

        // One consent write, with per-call benign-FORBIDDEN handling. A FORBIDDEN
        // from setSubscription can mean (a) MarketingTierNotActiveError — org not
        // marketing-writable, or (b) MarketingNotEntitledError — plan has no
        // Marketing. Both are BENIGN for consent: the contacts-db PUT above
        // already persisted the enum and there is no PF customer to resubscribe,
        // so consent HAS saved. (c) OverLimitError (PE-1623) — a usage-cap
        // rejection for a marketing-ENABLED org; swallowing THAT would hide a
        // genuine failure behind a success toast. The errorFormatter attaches
        // `data.overLimit` ONLY for (c), so it's the discriminator. This is
        // per-call (not wrapped around both awaits) so a benign result on the
        // first channel never skips the second channel's write (Greptile #2213).
        const applyChannel = async (
          subscribed: boolean,
          ch: 'email' | 'sms' | 'both',
        ): Promise<void> => {
          try {
            await setContactSubscription(contactId, subscribed, ch);
          } catch (consentErr) {
            const data = (consentErr as { data?: { code?: string; overLimit?: unknown } } | null)
              ?.data;
            const isBenignTierForbidden = data?.code === 'FORBIDDEN' && data?.overLimit == null;
            if (isBenignTierForbidden) {
              console.info(
                `Contact consent (${ch}) left to contacts-db write (org not marketing-writable / not entitled).`,
              );
              return;
            }
            // A genuine consent-write failure (over-limit, network, 500, PF
            // error) must NOT surface as a successful save — the re-grant did not
            // durably land. Re-throw into the outer catch (error toast, drawer
            // stays open) so the admin knows to retry.
            console.error(`Error syncing contact consent (${ch}) to marketing:`, consentErr);
            throw new Error(
              'Contact details saved, but updating communication consent failed. Please retry.',
            );
          }
        };

        if (emailChanged && smsChanged && nextEmail === nextSms) {
          // Both channels moved the same direction → one 'both' call (also the
          // only path that clears a FULL scope on a single request).
          await applyChannel(nextEmail, 'both');
        } else {
          // Per-changed-channel. A single-channel grant also lifts a full PF
          // scope (marketing planChannelConsent fix) so either toggle clears a
          // 'future'/'all'/'past' opt-out + resubscribes. Each call is
          // independent: a benign FORBIDDEN on one does not skip the other.
          if (emailChanged) await applyChannel(nextEmail, 'email');
          if (smsChanged) await applyChannel(nextSms, 'sms');
        }
      }

      // Success handling
      toast.success(isEditMode ? 'Contact updated successfully!' : 'Contact created successfully!');

      if (!isEditMode) {
        trackDashboardPlg(posthog, DASHBOARD_PLG_EVENTS.CONTACT_CREATED, {
          source: 'contacts_drawer',
        });
      }

      // Call the callback with the saved contact
      if (onContactSaved) {
        // Update the contact data with new response data, preserving existing fields
        const updatedContact = {
          ...contact,
          ...responseData.contact,
        };
        onContactSaved(updatedContact);
      }

      initialFormSnapshotRef.current = JSON.stringify(formData);
      onDirtyChange?.(false);

      // Close the drawer
      setOpen(false);
      clearForm();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitLatest = useLatestCallback(handleSubmit);

  // PE-1870: admin attests the donor separately confirmed texting START after
  // a carrier-level SMS STOP block. Fires ONLY from the confirm dialog's
  // explicit Confirm click -- never from a plain toggle. On success, unlock
  // the SMS toggle for the rest of this drawer session (local UI state --
  // never mutates the `contact` prop) and mark it subscribed to match what
  // the backend just wrote.
  const handleConfirmAdminVerifiedStart = async () => {
    if (!contact?.id) return;
    setAdminVerifiedStartPending(true);
    try {
      await setContactSubscriptionAdminVerifiedStart(contact.id);
      setFormData((prev) => ({ ...prev, smsConsent: 'SUBSCRIBED' }));
      setSmsCarrierLockCleared(true);
      toast.success('SMS block cleared for this contact.');
      setAdminVerifiedStartDialogOpen(false);
    } catch (err) {
      console.error('Error clearing SMS carrier-STOP override:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to clear the SMS block. Please try again.',
      );
    } finally {
      setAdminVerifiedStartPending(false);
    }
  };

  // PE-1870: true while the SMS toggle should render locked -- a genuine
  // carrier-level STOP scope on the contact, unless this drawer session
  // already cleared it via the verified-START override above.
  const smsCarrierLocked = isPocketflowSmsScoped(contact) && !smsCarrierLockCleared;

  // Clear form data
  const clearForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      organizationName: '',
      emails: [],
      phones: [],
      addresses: [],
    });
    setInvalidFields(new Set());
    setEmailTakenContactIDs({});
    setActiveTab('basic_info'); // Reset to first tab
  };

  // Handle address autocomplete
  const handleAddressChange = (e: any, addressIndex: number = 0) => {
    const { features } = e;
    if (features && features.length > 0) {
      const address = features[0].properties;

      const countryCode = address.country_code?.toLowerCase() ?? '';
      const countryObj = countries.find((c: any) => c.value.toLowerCase() === countryCode);

      // PE-1878: resolve the ISO code against the canonical options; codes with
      // no option may still normalize (e.g. 'il' -> 'Palestine'). Anything else
      // stays empty rather than misattributing to a default country.
      const normalizedCode = normalizeCountry(countryCode);
      const countryName = countryObj
        ? countryObj.label
        : normalizedCode !== countryCode
          ? normalizedCode
          : '';
      setFormData((prevData) => ({
        ...prevData,
        addresses: prevData.addresses.map((addr, index) =>
          index === addressIndex
            ? {
                ...addr,
                line1: address.address_line1 || '',
                line2: address.address_line2 || '',
                city: address.city || address.place || '',
                region: address.state_code || address.state || address.region_code || '',
                postal: address.postcode || '',
                country: countryName || '', // Use full country name instead of code
              }
            : addr,
        ),
      }));
    }
  };

  // Handle drawer close
  const handleClose = () => {
    setOpen(false);
    clearForm();
  };

  useEffect(() => {
    if (presentation !== 'workspace' || !setWorkspaceActions) return undefined;

    setWorkspaceActions([
      {
        key: 'save',
        label: isSubmitting ? 'Saving...' : isEditMode ? 'Update Contact' : 'Create Contact',
        variant: 'primary',
        disabled: isSubmitting || invalidFields.size > 0,
        onClick: handleSubmitLatest,
      },
    ]);

    return () => setWorkspaceActions?.(null);
    // Register current workspace action state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    presentation,
    setWorkspaceActions,
    isSubmitting,
    invalidFields.size,
    isEditMode,
    handleSubmitLatest,
  ]);

  const handlePointerDownOutside = (event: any) => {
    // Check if the click is within any element with a class starting with 'mbx'
    if (event.target.closest('[class^="mbx"]')) {
      event.preventDefault(); // Prevent the dialog from closing
    }
  };

  // Handle input changes with real-time validation
  const handleInputChange = (
    fieldType: 'email' | 'phone' | 'address',
    index: number,
    field: string,
    value: string,
  ) => {
    setFormData((prev) => {
      const updatedData = { ...prev };

      if (fieldType === 'email') {
        updatedData.emails = prev.emails.map((email, i) =>
          i === index ? { ...email, [field]: value } : email,
        );
      } else if (fieldType === 'phone') {
        updatedData.phones = prev.phones.map((phone, i) =>
          i === index ? { ...phone, [field]: value } : phone,
        );
      } else if (fieldType === 'address') {
        updatedData.addresses = prev.addresses.map((address, i) =>
          i === index ? { ...address, [field]: value } : address,
        );
      }

      return updatedData;
    });

    // Clear validation errors when user starts typing
    if (fieldType === 'email') {
      if (isFieldInvalid(`email-invalid-${index}`)) {
        handleFieldFocus(`email-invalid-${index}`);
      }
      // email-taken comes from the backend on submit (per-merchant
      // uniqueness collision). Clear it as soon as the user changes
      // this email's value so the inline error doesn't lie about the
      // current input. The duplicate-checking effect only prunes
      // `-invalid-` / `-duplicate-` ids, so we have to clear taken
      // here ourselves.
      if (field === 'value' && isFieldInvalid(`email-taken-${index}`)) {
        setInvalidFields((prev) => {
          const next = new Set(prev);
          next.delete(`email-taken-${index}`);
          return next;
        });
        setEmailTakenContactIDs((prev) => {
          if (!(index in prev)) return prev;
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }
      // For duplicate errors, we'll let the real-time validation handle clearing them
      // when the duplication is actually resolved
    } else if (fieldType === 'phone') {
      if (isFieldInvalid(`phone-invalid-${index}`)) {
        handleFieldFocus(`phone-invalid-${index}`);
      }
      // For duplicate errors, we'll let the real-time validation handle clearing them
      // when the duplication is actually resolved
    } else if (fieldType === 'address') {
      // For duplicate errors, we'll let the real-time validation handle clearing them
      // when the duplication is actually resolved
    }
  };

  // Handle adding new items
  const handleAddItem = (itemType: 'email' | 'phone' | 'address') => {
    setFormData((prev) => {
      if (itemType === 'email') {
        return {
          ...prev,
          emails: [
            ...prev.emails,
            {
              value: '',
              is_primary: prev.emails.length === 0,
              new: isEditMode,
            },
          ],
        };
      } else if (itemType === 'phone') {
        return {
          ...prev,
          phones: [
            ...prev.phones,
            {
              value: '',
              is_primary: prev.phones.length === 0,
              new: isEditMode,
            },
          ],
        };
      } else if (itemType === 'address') {
        return {
          ...prev,
          addresses: [
            ...prev.addresses,
            {
              line1: '',
              line2: '',
              city: '',
              region: '',
              postal: '',
              country: '',
              is_primary: prev.addresses.length === 0,
              new: isEditMode,
            },
          ],
        };
      }
      return prev;
    });
  };

  // Computed values for active items (non-deleted)
  const activeEmails = formData.emails.filter((email) => !email.deleted);
  const activePhones = formData.phones.filter((phone) => !phone.deleted);
  const activeAddresses = formData.addresses.filter((address) => !address.deleted);
  const primaryEmail = activeEmails.find((email) => email.is_primary)?.value;
  const primaryPhone = activePhones.find((phone) => phone.is_primary)?.value;
  const contactTitle =
    [formData.firstName, formData.lastName].filter(Boolean).join(' ') ||
    formData.organizationName ||
    (isEditMode ? 'Contact' : 'New contact');

  return (
    <>
      <Sheet embedded={presentation === 'workspace'} open={open} onOpenChange={setOpen}>
        <SheetContent
          embedded={presentation === 'workspace'}
          side="bottom"
          className={
            presentation === 'workspace'
              ? 'h-full overflow-hidden rounded-none bg-white p-0'
              : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
          }
          onPointerDownOutside={handlePointerDownOutside}
        >
          {/* Main Container - centered */}
          <div
            className={`h-full w-full ${
              presentation === 'workspace'
                ? 'overflow-y-auto bg-white'
                : 'pointer-events-none mx-auto max-w-[1600px]'
            }`}
          >
            {/* Header with Action Buttons */}
            {presentation !== 'workspace' && (
              <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
                <div className="flex items-start space-x-3">
                  <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="size-6"
                    >
                      <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.882 0-5.208-.799-6.761-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-base text-ink-muted">
                      {isEditMode ? 'Edit Contact' : 'Create Contact'}
                    </p>
                    <p className="text-sm text-ink-muted">
                      {isEditMode
                        ? 'Update contact information and details.'
                        : 'Add a new contact to your database.'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
                    Close
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || invalidFields.size > 0}
                    variant="primary"
                    size="workspace"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>Saving...</span>
                      </div>
                    ) : isEditMode ? (
                      'Update Contact'
                    ) : (
                      'Create Contact'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {presentation !== 'workspace' && <Separator className="mb-3 bg-paper-inset" />}

            {presentation === 'workspace' && (
              <EntityIdentityHeader
                title={contactTitle}
                identifier={contact?.id}
                identifierLabel="Contact ID"
                metadata={[
                  { key: 'email', value: primaryEmail },
                  { key: 'phone', value: primaryPhone },
                ]}
                statuses={
                  isEditMode
                    ? [
                        {
                          key: 'email-consent',
                          label:
                            formData.emailConsent === 'SUBSCRIBED'
                              ? 'Email subscribed'
                              : 'Email unsubscribed',
                          variant: formData.emailConsent === 'SUBSCRIBED' ? 'success' : 'neutral',
                        },
                        {
                          key: 'sms-consent',
                          label:
                            formData.smsConsent === 'SUBSCRIBED'
                              ? 'SMS subscribed'
                              : 'SMS unsubscribed',
                          variant: formData.smsConsent === 'SUBSCRIBED' ? 'success' : 'neutral',
                        },
                      ]
                    : []
                }
                actions={
                  <>
                    <Button
                      type="button"
                      variant="primary"
                      size="workspace"
                      onClick={handleSubmitLatest}
                      disabled={isSubmitting || invalidFields.size > 0}
                      className={HEADER_ACTION_BUTTON_CLASS}
                    >
                      {isSubmitting ? 'Saving…' : isEditMode ? 'Save changes' : 'Create contact'}
                    </Button>
                    <HeaderCloseAction
                      label={isEditMode ? 'Exit contact editor' : 'Exit contact creation'}
                      onClick={handleClose}
                      disabled={isSubmitting}
                    />
                  </>
                }
              >
                <details className="mt-3 border-t border-edge pt-3 lg:hidden">
                  <summary className="cursor-pointer text-xs text-ink-secondary">Editor details</summary>
                  <p className="mt-2 text-xs text-ink-muted">An email or a phone number is required.</p>
                </details>
                <div className="hidden lg:block">
                <RecordDetailsSection
                  title={isEditMode ? 'Contact editor' : 'Contact setup'}
                  details={[
                    {
                      key: 'mode',
                      label: 'Mode',
                      value: isEditMode ? 'Editing existing contact' : 'Creating a new contact',
                    },
                    {
                      key: 'required',
                      label: 'Required information',
                      value: 'An email or a phone number',
                    },
                  ]}
                />
                </div>
              </EntityIdentityHeader>
            )}

            {/* Form Content */}
            <div
              className={`compact-editor-controls pointer-events-auto w-full overflow-hidden ${
                presentation === 'workspace' ? 'min-h-0' : 'h-[calc(100%-80px)]'
              }`}
            >
              <div
                className={
                  presentation === 'workspace'
                    ? 'pointer-events-auto'
                    : 'pointer-events-auto h-full overflow-y-hidden'
                }
              >
                <div
                  className={
                    presentation === 'workspace'
                      ? 'grid w-full items-start gap-4 px-3 py-4 md:px-5 xl:grid-cols-[minmax(0,1fr)_340px]'
                      : 'flex h-full w-full flex-col gap-4 md:flex-row'
                  }
                >
                  {presentation !== 'workspace' && (
                    <div className="w-full md:w-1/2">
                      {/* Contact Info Card */}
                      <div className="rounded-lg border border-edge bg-paper-muted p-4 text-sm font-bold text-ink-secondary shadow-sm">
                        <div className="space-y-1">
                          {/* Full Name */}
                          <div className="flex items-center gap-1">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full">
                              <svg
                                width="21"
                                height="21"
                                viewBox="0 0 21 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M6.74997 5.82056C6.74997 3.74949 8.4289 2.07056 10.5 2.07056C12.571 2.07056 14.25 3.74949 14.25 5.82056C14.25 7.89162 12.571 9.57056 10.5 9.57056C8.4289 9.57056 6.74997 7.89162 6.74997 5.82056Z"
                                  fill="#6B6458"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M3.62601 17.575C3.69046 13.8335 6.74329 10.8206 10.5 10.8206C14.2567 10.8206 17.3096 13.8337 17.3739 17.5753C17.3782 17.8234 17.2352 18.0506 17.0097 18.1541C15.0272 19.0637 12.8221 19.5706 10.5002 19.5706C8.17819 19.5706 5.97283 19.0636 3.99024 18.1538C3.76468 18.0503 3.62173 17.8231 3.62601 17.575Z"
                                  fill="#6B6458"
                                />
                              </svg>
                            </div>
                            <p>
                              {formData.firstName && formData.lastName
                                ? `${formData.firstName} ${formData.lastName}`
                                : formData.firstName || formData.lastName || 'Not provided'}
                            </p>
                          </div>

                          {/* Organization Name */}
                          <div className="flex items-center gap-1">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full">
                              <svg
                                width="21"
                                height="21"
                                viewBox="0 0 21 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.1534 2.80053C10.3633 2.66057 10.6368 2.66057 10.8468 2.80053L18.3468 7.80053C18.634 7.992 18.7116 8.38004 18.5201 8.66724C18.3286 8.95445 17.9406 9.03206 17.6534 8.84059L10.5001 4.07171L3.34675 8.84059C3.05955 9.03206 2.67151 8.95445 2.48003 8.66724C2.28856 8.38004 2.36617 7.992 2.65338 7.80053L10.1534 2.80053Z"
                                  fill="#6B6458"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M17.375 9.43097V17.6956H18C18.3452 17.6956 18.625 17.9754 18.625 18.3206C18.625 18.6657 18.3452 18.9456 18 18.9456H3C2.65482 18.9456 2.375 18.6657 2.375 18.3206C2.375 17.9754 2.65482 17.6956 3 17.6956H3.625V9.43097C3.625 9.1232 3.84906 8.86124 4.15312 8.81353C6.22148 8.48899 8.34128 8.32056 10.5 8.32056C12.6587 8.32056 14.7785 8.48899 16.8469 8.81353C17.1509 8.86124 17.375 9.1232 17.375 9.43097ZM11.125 11.4456C11.125 11.1004 10.8452 10.8206 10.5 10.8206C10.1548 10.8206 9.875 11.1004 9.875 11.4456V17.0706C9.875 17.4157 10.1548 17.6956 10.5 17.6956C10.8452 17.6956 11.125 17.4157 11.125 17.0706V11.4456ZM13.625 10.8206C13.9702 10.8206 14.25 11.1004 14.25 11.4456V17.0706C14.25 17.4157 13.9702 17.6956 13.625 17.6956C13.2798 17.6956 13 17.4157 13 17.0706V11.4456C13 11.1004 13.2798 10.8206 13.625 10.8206ZM8 11.4456C8 11.1004 7.72018 10.8206 7.375 10.8206C7.02982 10.8206 6.75 11.1004 6.75 11.4456V17.0706C6.75 17.4157 7.02982 17.6956 7.375 17.6956C7.72018 17.6956 8 17.4157 8 17.0706V11.4456Z"
                                  fill="#6B6458"
                                />
                                <path
                                  d="M10.5 7.38306C11.0178 7.38306 11.4375 6.96332 11.4375 6.44556C11.4375 5.92779 11.0178 5.50806 10.5 5.50806C9.98223 5.50806 9.5625 5.92779 9.5625 6.44556C9.5625 6.96332 9.98223 7.38306 10.5 7.38306Z"
                                  fill="#6B6458"
                                />
                              </svg>
                            </div>
                            {formData.organizationName ? (
                              <p>{formData.organizationName}</p>
                            ) : (
                              <p>Not provided</p>
                            )}
                          </div>

                          {/* Primary Email */}
                          <div className="flex items-center gap-1">
                            <div className="flex h-8 w-8 items-center justify-center">
                              <svg
                                width="21"
                                height="21"
                                viewBox="0 0 21 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1.75 8.0448V15.1956C1.75 16.5763 2.86929 17.6956 4.25 17.6956H16.75C18.1307 17.6956 19.25 16.5763 19.25 15.1956V8.0448L11.8102 12.6231C11.0067 13.1176 9.99327 13.1176 9.18976 12.6231L1.75 8.0448Z"
                                  fill="#6B6458"
                                />
                                <path
                                  d="M19.25 6.57708V6.44556C19.25 5.06484 18.1307 3.94556 16.75 3.94556H4.25C2.86929 3.94556 1.75 5.06484 1.75 6.44556V6.57708L9.84488 11.5585C10.2466 11.8058 10.7534 11.8058 11.1551 11.5585L19.25 6.57708Z"
                                  fill="#6B6458"
                                />
                              </svg>
                            </div>
                            {formData.emails.find((email) => email.is_primary && !email.deleted)
                              ?.value ? (
                              <p>
                                {
                                  formData.emails.find(
                                    (email) => email.is_primary && !email.deleted,
                                  )?.value
                                }
                              </p>
                            ) : (
                              <p>Not provided</p>
                            )}
                          </div>

                          {/* Primary Phone */}
                          <div className="flex items-center gap-1">
                            <div className="flex h-8 w-8 items-center justify-center">
                              <svg
                                width="21"
                                height="21"
                                viewBox="0 0 21 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M1.75 4.57056C1.75 3.18984 2.86929 2.07056 4.25 2.07056H5.39302C6.11 2.07056 6.73498 2.55852 6.90887 3.25409L7.83037 6.94008C7.98284 7.54997 7.75495 8.19185 7.25202 8.56904L6.174 9.37756C6.06206 9.46151 6.03772 9.58446 6.06917 9.67029C7.01542 12.2535 9.0671 14.3051 11.6503 15.2514C11.7361 15.2828 11.859 15.2585 11.943 15.1466L12.7515 14.0685C13.1287 13.5656 13.7706 13.3377 14.3805 13.4902L18.0665 14.4117C18.762 14.5856 19.25 15.2106 19.25 15.9275V17.0706C19.25 18.4513 18.1307 19.5706 16.75 19.5706H14.875C7.62626 19.5706 1.75 13.6943 1.75 6.44556V4.57056Z"
                                  fill="#6B6458"
                                />
                              </svg>
                            </div>
                            {formData.phones.find((phone) => phone.is_primary && !phone.deleted)
                              ?.value ? (
                              <p>
                                {
                                  formData.phones.find(
                                    (phone) => phone.is_primary && !phone.deleted,
                                  )?.value
                                }
                              </p>
                            ) : (
                              <p>Not provided</p>
                            )}
                          </div>

                          {/* Primary Address */}

                          <div className="flex items-center gap-1">
                            <div className="flex h-8 w-8 items-center justify-center">
                              <svg
                                width="21"
                                height="21"
                                viewBox="0 0 21 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.0581 4.02138C10.3021 3.7773 10.6979 3.7773 10.9419 4.02138L18.1831 11.2625C18.4271 11.5066 18.8229 11.5066 19.0669 11.2625C19.311 11.0184 19.311 10.6227 19.0669 10.3786L11.8258 3.13749C11.0936 2.40526 9.90641 2.40526 9.17417 3.13749L1.93306 10.3786C1.68898 10.6227 1.68898 11.0184 1.93306 11.2625C2.17714 11.5066 2.57286 11.5066 2.81694 11.2625L10.0581 4.02138Z"
                                  fill="#6B6458"
                                />
                                <path
                                  d="M10.5 5.3472L17.2992 12.1464C17.3239 12.1711 17.3492 12.1951 17.375 12.2182V17.383C17.375 18.246 16.6754 18.9455 15.8125 18.9455H13C12.6548 18.9455 12.375 18.6657 12.375 18.3205V14.5705C12.375 14.2254 12.0952 13.9455 11.75 13.9455H9.25C8.90482 13.9455 8.625 14.2254 8.625 14.5705V18.3205C8.625 18.6657 8.34518 18.9455 8 18.9455H5.1875C4.32456 18.9455 3.625 18.246 3.625 17.383V12.2182C3.65077 12.1951 3.67606 12.1711 3.70083 12.1464L10.5 5.3472Z"
                                  fill="#6B6458"
                                />
                              </svg>
                            </div>
                            {(() => {
                              const primaryAddress = formData.addresses.find(
                                (address) => address.is_primary && !address.deleted,
                              );

                              if (!primaryAddress) {
                                return <p>Not provided</p>;
                              }

                              const addressParts = [
                                primaryAddress.line1,
                                primaryAddress.line2,
                                primaryAddress.city,
                                primaryAddress.region,
                                primaryAddress.postal,
                                primaryAddress.country,
                              ].filter(Boolean);

                              return addressParts.length > 0 ? (
                                <p>{addressParts.join(', ')}</p>
                              ) : (
                                <p>Not provided</p>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    className={
                      presentation === 'workspace'
                        ? 'contents'
                        : 'flex h-full w-full flex-col gap-4 overflow-hidden md:w-1/2'
                    }
                  >
                    {/* Tab Navigation */}
                    {presentation !== 'workspace' && (
                      <div className="border-edge bg-white">
                        <div className="sm:hidden">
                          <label htmlFor="tabs" className="sr-only">
                            Select a tab
                          </label>
                          <FormSelect
                            id="tabs"
                            name="tabs"
                            className="block border-edge py-2 pl-3 pr-10 text-base focus:border-accent focus:ring-accent sm:text-sm"
                            value={activeTab}
                            onChange={(e) => setActiveTab(e.target.value as any)}
                          >
                            {visibleTabs.map((tab) => (
                              <option key={tab.id} value={tab.id}>
                                {tab.name}
                              </option>
                            ))}
                          </FormSelect>
                        </div>
                        <div className="hidden sm:block">
                          <nav
                            className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                            aria-label="Tabs"
                          >
                            {visibleTabs.map((tab) => (
                              <button
                                type="button"
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={classNames(
                                  activeTab === tab.id
                                    ? 'border-ink-secondary text-ink-secondary'
                                    : 'border-transparent text-ink-muted hover:text-ink-secondary',
                                  'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                                )}
                              >
                                {tab.name}
                              </button>
                            ))}
                          </nav>
                        </div>
                      </div>
                    )}

                    {/* Content wrapper with spacing */}
                    <div
                      className={
                        presentation === 'workspace' ? 'contents' : 'flex-1 overflow-y-auto py-4'
                      }
                    >
                      {/* Basic Information Tab */}
                      <ContactEditorSection
                        workspace={presentation === 'workspace'}
                        title="Contact name"
                        drawerTitle="Contact Name"
                        description="Update the contact's name and organization."
                        icon={UserIcon}
                        className={
                          presentation === 'workspace'
                            ? 'xl:col-start-1'
                            : activeTab === 'basic_info'
                              ? 'space-y-4'
                              : 'hidden'
                        }
                      >
                        <div className="mb-4 h-fit space-y-3 rounded-md border-edge bg-paper-inset p-2">
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {/* First Name */}
                            <div>
                              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                First Name (Optional)
                              </Label>
                              <Input
                                id="firstName"
                                name="firstName"
                                type="text"
                                value={formData.firstName}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    firstName: e.target.value,
                                  }))
                                }
                                className="h-7 border border-edge bg-white"
                                placeholder="Enter first name"
                              />
                            </div>

                            {/* Last Name */}
                            <div>
                              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                Last Name (Optional)
                              </Label>
                              <Input
                                id="lastName"
                                name="lastName"
                                type="text"
                                value={formData.lastName}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    lastName: e.target.value,
                                  }))
                                }
                                className="h-7 border border-edge bg-white"
                                placeholder="Enter last name"
                              />
                            </div>
                          </div>

                          {/* Organization Name */}
                          <div>
                            <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                              Organization Name (Optional)
                            </Label>
                            <Input
                              id="organizationName"
                              name="organizationName"
                              type="text"
                              value={formData.organizationName}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  organizationName: e.target.value,
                                }))
                              }
                              className="h-7 border border-edge bg-white"
                              placeholder="Enter organization name"
                              autoComplete="organization"
                            />
                          </div>
                        </div>
                      </ContactEditorSection>

                      {/* Email & Phone Tab */}
                      <ContactEditorSection
                        workspace={presentation === 'workspace'}
                        title="Contact methods"
                        description="Manage email addresses, phone numbers, and communication consent."
                        icon={EnvelopeIcon}
                        showDrawerHeader={false}
                        className={
                          presentation === 'workspace'
                            ? 'xl:col-start-1'
                            : activeTab === 'contact_details'
                              ? 'space-y-4'
                              : 'hidden'
                        }
                      >
                        {/* Invalid-contact-details indicator. Surfaced at
                          the top of the Email & Phone tab because that's
                          where the user fixes the problem (PF rejects on
                          email or phone format). Reads from the original
                          `contact` prop, not `formData`, because the
                          sync-error attributes are read-only (written by
                          the marketing-backend on rejection, REMOVEd
                          atomically on the next successful sync) and the
                          dashboard never edits them. The Contact interface
                          omits these fields on purpose to keep the editable
                          shape narrow — cast inline. Copy is derived from
                          the categorized error fields (a contact can be
                          rejected on BOTH email and phone; the free-text
                          message only names the last rejection) and never
                          mentions marketing/sync — see contactSyncError.ts. */}
                        {(() => {
                          const syncError = contactSyncErrorSummary(
                            (contact ?? {}) as {
                              pocketflow_sync_error?: unknown;
                              pocketflow_sync_error_fields?: unknown;
                            },
                          );
                          return (
                            syncError && (
                              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                <div className="font-medium">{syncError.title}</div>
                                <div className="text-xs text-amber-800">{syncError.detail}</div>
                              </div>
                            )
                          );
                        })()}
                        {channelError && (
                          <p role="alert" className="text-xs text-status-red-text">
                            {channelError}
                          </p>
                        )}
                        <ContactEditorSubheading
                          workspace={presentation === 'workspace'}
                          title="Email"
                        />

                        {/* Email Empty State or Email List */}
                        {activeEmails.length === 0 ? (
                          <EmptyState
                            icon={<EnvelopeIcon className="h-6 w-6 text-ink-muted" />}
                            title="No emails added"
                            description="Add an email address to stay in touch with this contact."
                            actionLabel="Add Email"
                            onAction={() => handleAddItem('email')}
                          />
                        ) : (
                          <div className="mb-4 w-full">
                            {/* Emails Section */}

                            <div className="h-fit space-y-2">
                              {formData.emails.map((email, index) => (
                                <div
                                  key={index}
                                  className={`space-y-2 rounded-md border-edge bg-paper-inset p-2 ${
                                    isEditMode && email.deleted
                                      ? 'border-red-200 bg-[#F5EDEB] opacity-50'
                                      : isEditMode && email.new
                                        ? 'border-accent/30 bg-accent/10'
                                        : ''
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                      {email.is_primary ? 'Primary Email' : 'Secondary Email'}
                                      {isEditMode && email.deleted && (
                                        <span className="ml-2 text-xs font-normal text-red-500">
                                          (Deleted)
                                        </span>
                                      )}
                                      {isEditMode && email.new && !email.deleted && (
                                        <span className="ml-2 text-xs font-normal text-accent">
                                          (New)
                                        </span>
                                      )}
                                    </Label>
                                    <div className="flex items-center gap-2">
                                      {formData.emails.length > 1 && !email.deleted && (
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            className="flex items-center gap-1"
                                            onClick={() => {
                                              setFormData((prev) => ({
                                                ...prev,
                                                emails: prev.emails.map((em, i) => ({
                                                  ...em,
                                                  is_primary: i === index,
                                                })),
                                              }));
                                            }}
                                          >
                                            <span
                                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                                                email.is_primary ? 'border-sage' : 'border-edge'
                                              }`}
                                              aria-hidden
                                            >
                                              {email.is_primary && (
                                                <span className="h-2 w-2 rounded-full bg-sage" />
                                              )}
                                            </span>
                                            <span className="text-xs text-ink-muted">Primary</span>
                                          </button>
                                        </div>
                                      )}
                                      {!email.deleted && (
                                        <Button
                                          type="button"
                                          variant="dropdown"
                                          size="sm"
                                          onClick={() => {
                                            setFormData((prev) => {
                                              // In create mode, always fully remove items
                                              if (!isEditMode) {
                                                const newEmails = prev.emails.filter(
                                                  (_, i) => i !== index,
                                                );
                                                // If we're removing the primary email, make the first remaining email primary
                                                if (email.is_primary && newEmails.length > 0) {
                                                  newEmails[0].is_primary = true;
                                                }
                                                return {
                                                  ...prev,
                                                  emails: newEmails,
                                                };
                                              }

                                              // In edit mode, handle new vs existing items
                                              if (email.new) {
                                                const newEmails = prev.emails.filter(
                                                  (_, i) => i !== index,
                                                );
                                                // If we're removing the primary email and it was new, make the first remaining email primary
                                                if (email.is_primary && newEmails.length > 0) {
                                                  newEmails[0].is_primary = true;
                                                }
                                                return {
                                                  ...prev,
                                                  emails: newEmails,
                                                };
                                              }

                                              // Otherwise, soft-delete existing items in edit mode
                                              const updatedEmails = prev.emails.map((em, i) => {
                                                if (i === index) {
                                                  return {
                                                    ...em,
                                                    deleted: true,
                                                    is_primary: false,
                                                  };
                                                }
                                                return em;
                                              });

                                              // If we're soft-deleting the primary email, make the first remaining active email primary
                                              if (email.is_primary) {
                                                const activeEmails = updatedEmails.filter(
                                                  (em) => !em.deleted,
                                                );
                                                if (activeEmails.length > 0) {
                                                  activeEmails[0].is_primary = true;
                                                }
                                              }

                                              return {
                                                ...prev,
                                                emails: updatedEmails,
                                              };
                                            });
                                          }}
                                          className="h-6 w-6 rounded-full p-0 text-xs hover:bg-paper-inset"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M18 6L6 18" />
                                            <path d="M6 6l12 12" />
                                          </svg>
                                        </Button>
                                      )}
                                      {isEditMode && email.deleted && (
                                        <Button
                                          type="button"
                                          variant="dropdown"
                                          size="sm"
                                          onClick={() => {
                                            setFormData((prev) => {
                                              const updatedEmails = prev.emails.map((em, i) => {
                                                if (i === index) {
                                                  // Remove the deleted flag
                                                  const { deleted, ...rest } = em;
                                                  return rest;
                                                }
                                                return em;
                                              });

                                              // If there's no primary email after restoration, make this one primary
                                              const hasPrimary = updatedEmails.some(
                                                (em) => em.is_primary,
                                              );
                                              if (!hasPrimary) {
                                                updatedEmails[index].is_primary = true;
                                              }

                                              return {
                                                ...prev,
                                                emails: updatedEmails,
                                              };
                                            });
                                          }}
                                          className="h-6 w-6 rounded-full bg-sage-light p-0 text-xs hover:bg-[#E2EBE2]"
                                          title="Restore email"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                            <path d="M21 3v5h-5" />
                                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                            <path d="M3 21v-5h5" />
                                          </svg>
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="email"
                                      name={`email-${index}`}
                                      value={email.value}
                                      onChange={(e) =>
                                        handleInputChange('email', index, 'value', e.target.value)
                                      }
                                      onFocus={() => {
                                        handleFieldFocus(`email-invalid-${index}`);
                                      }}
                                      className={`h-7 border bg-white ${
                                        isFieldInvalid(`email-invalid-${index}`) ||
                                        isFieldInvalid(`email-duplicate-${index}`) ||
                                        isFieldInvalid(`email-taken-${index}`)
                                          ? 'border-red-500'
                                          : 'border-edge'
                                      }`}
                                      placeholder="Enter email address"
                                      disabled={email.deleted}
                                    />
                                  </div>
                                  {isFieldInvalid(`email-invalid-${index}`) && (
                                    <p className="text-xs text-red-500">
                                      Please enter a valid email address
                                    </p>
                                  )}
                                  {isFieldInvalid(`email-duplicate-${index}`) && (
                                    <p className="text-xs text-red-500">
                                      This email address is already added
                                    </p>
                                  )}
                                  {isFieldInvalid(`email-taken-${index}`) && (
                                    <div className="space-y-1">
                                      <p className="text-xs text-red-500">
                                        This email is already used by another contact at this
                                        merchant.
                                      </p>
                                      {emailTakenContactIDs[index] && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            // Navigate to the contacts list with the
                                            // owning contact pre-filtered. Donors.js
                                            // consumes ?contact=<id>, hydrates the
                                            // keyword filter from /validate/contact,
                                            // then strips the param. Close the drawer
                                            // so the underlying page is visible.
                                            const targetId = emailTakenContactIDs[index];
                                            setOpen(false);
                                            history.push(`/contacts?contact=${targetId}`);
                                          }}
                                          className="text-xs font-medium text-red-600 underline hover:text-red-700"
                                        >
                                          View contact
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="mt-2 flex w-full justify-end">
                              <button
                                type="button"
                                onClick={() => handleAddItem('email')}
                                className={`flex items-center text-sm font-medium text-ink-muted hover:text-ink-secondary`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <path d="M12 5v14" />
                                  <path d="M5 12h14" />
                                </svg>
                                Add Email
                              </button>
                            </div>
                          </div>
                        )}

                        <ContactEditorSubheading
                          workspace={presentation === 'workspace'}
                          title="Phone"
                        />

                        {/* Phone Empty State or Phone List */}
                        {activePhones.length === 0 ? (
                          <EmptyState
                            icon={<PhoneIcon className="h-6 w-6 text-ink-muted" />}
                            title="No phone numbers added"
                            description="Add a phone number to call or text this contact."
                            actionLabel="Add Phone"
                            onAction={() => handleAddItem('phone')}
                          />
                        ) : (
                          <div className="w-full">
                            {/* Phones Section */}
                            <div className="h-fit space-y-2">
                              {formData.phones.map((phone, index) => (
                                <div
                                  key={index}
                                  className={`space-y-2 rounded-md border-edge bg-paper-inset p-2 ${
                                    isEditMode && phone.deleted
                                      ? 'border-red-200 bg-[#F5EDEB] opacity-50'
                                      : isEditMode && phone.new
                                        ? 'border-accent/30 bg-accent/10'
                                        : ''
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                      {phone.is_primary ? 'Primary Phone' : 'Secondary Phone'}
                                      {isEditMode && phone.deleted && (
                                        <span className="ml-2 text-xs font-normal text-red-500">
                                          (Deleted)
                                        </span>
                                      )}
                                      {isEditMode && phone.new && !phone.deleted && (
                                        <span className="ml-2 text-xs font-normal text-accent">
                                          (New)
                                        </span>
                                      )}
                                    </Label>
                                    <div className="flex items-center gap-2">
                                      {formData.phones.length > 1 && !phone.deleted && (
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            className="flex items-center gap-1"
                                            onClick={() => {
                                              setFormData((prev) => ({
                                                ...prev,
                                                phones: prev.phones.map((ph, i) => ({
                                                  ...ph,
                                                  is_primary: i === index,
                                                })),
                                              }));
                                            }}
                                          >
                                            <span
                                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                                                phone.is_primary ? 'border-sage' : 'border-edge'
                                              }`}
                                              aria-hidden
                                            >
                                              {phone.is_primary && (
                                                <span className="h-2 w-2 rounded-full bg-sage" />
                                              )}
                                            </span>
                                            <span className="text-xs text-ink-muted">Primary</span>
                                          </button>
                                        </div>
                                      )}
                                      {!phone.deleted && (
                                        <Button
                                          type="button"
                                          variant="dropdown"
                                          size="sm"
                                          onClick={() => {
                                            setFormData((prev) => {
                                              // In create mode, always fully remove items
                                              if (!isEditMode) {
                                                const newPhones = prev.phones.filter(
                                                  (_, i) => i !== index,
                                                );
                                                // If we're removing the primary phone, make the first remaining phone primary
                                                if (phone.is_primary && newPhones.length > 0) {
                                                  newPhones[0].is_primary = true;
                                                }
                                                return {
                                                  ...prev,
                                                  phones: newPhones,
                                                };
                                              }

                                              // In edit mode, handle new vs existing items
                                              if (phone.new) {
                                                const newPhones = prev.phones.filter(
                                                  (_, i) => i !== index,
                                                );
                                                // If we're removing the primary phone and it was new, make the first remaining phone primary
                                                if (phone.is_primary && newPhones.length > 0) {
                                                  newPhones[0].is_primary = true;
                                                }
                                                return {
                                                  ...prev,
                                                  phones: newPhones,
                                                };
                                              }

                                              // Otherwise, soft-delete existing items in edit mode
                                              const updatedPhones = prev.phones.map((ph, i) => {
                                                if (i === index) {
                                                  return {
                                                    ...ph,
                                                    deleted: true,
                                                    is_primary: false,
                                                  };
                                                }
                                                return ph;
                                              });

                                              // If we're soft-deleting the primary phone, make the first remaining active phone primary
                                              if (phone.is_primary) {
                                                const activePhones = updatedPhones.filter(
                                                  (ph) => !ph.deleted,
                                                );
                                                if (activePhones.length > 0) {
                                                  activePhones[0].is_primary = true;
                                                }
                                              }

                                              return {
                                                ...prev,
                                                phones: updatedPhones,
                                              };
                                            });
                                          }}
                                          className="h-6 w-6 rounded-full p-0 text-xs hover:bg-paper-inset"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M18 6L6 18" />
                                            <path d="M6 6l12 12" />
                                          </svg>
                                        </Button>
                                      )}
                                      {isEditMode && phone.deleted && (
                                        <Button
                                          type="button"
                                          variant="dropdown"
                                          size="sm"
                                          onClick={() => {
                                            setFormData((prev) => {
                                              const updatedPhones = prev.phones.map((ph, i) => {
                                                if (i === index) {
                                                  // Remove the deleted flag
                                                  const { deleted, ...rest } = ph;
                                                  return rest;
                                                }
                                                return ph;
                                              });

                                              // If there's no primary phone after restoration, make this one primary
                                              const hasPrimary = updatedPhones.some(
                                                (ph) => ph.is_primary,
                                              );
                                              if (!hasPrimary) {
                                                updatedPhones[index].is_primary = true;
                                              }

                                              return {
                                                ...prev,
                                                phones: updatedPhones,
                                              };
                                            });
                                          }}
                                          className="h-6 w-6 rounded-full bg-sage-light p-0 text-xs hover:bg-[#E2EBE2]"
                                          title="Restore phone"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                            <path d="M21 3v5h-5" />
                                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                            <path d="M3 21v-5h5" />
                                          </svg>
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex w-full items-center gap-2">
                                    <PhoneInput
                                      country={'us'}
                                      value={phone.value}
                                      onChange={(phone) =>
                                        handleInputChange('phone', index, 'value', phone)
                                      }
                                      disableSearchIcon={true}
                                      buttonStyle={{
                                        borderRadius: '6px 0px 0px 6px',
                                        border:
                                          isFieldInvalid(`phone-invalid-${index}`) ||
                                          isFieldInvalid(`phone-duplicate-${index}`)
                                            ? '1px solid #ef4444'
                                            : '1px solid #E0D9CC',
                                      }}
                                      inputStyle={{
                                        width: '100%',
                                        height: '28px',
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: '6px',
                                        border:
                                          isFieldInvalid(`phone-invalid-${index}`) ||
                                          isFieldInvalid(`phone-duplicate-${index}`)
                                            ? '1px solid #ef4444'
                                            : '1px solid #E0D9CC',
                                      }}
                                      preferredCountries={['us', 'ca']}
                                      enableSearch={true}
                                      dropdownStyle={{
                                        width: 'full',
                                        borderRadius: '6px',
                                      }}
                                      searchStyle={{
                                        width: '93%',
                                        borderRadius: '6px',
                                      }}
                                      inputProps={{
                                        name: `phone-${index}`,
                                      }}
                                      onFocus={() => {
                                        handleFieldFocus(`phone-invalid-${index}`);
                                      }}
                                      placeholder="Enter phone number"
                                      disabled={phone.deleted}
                                    />
                                  </div>

                                  {isFieldInvalid(`phone-invalid-${index}`) && (
                                    <p className="text-xs text-red-500">
                                      Please enter a valid phone number
                                    </p>
                                  )}
                                  {isFieldInvalid(`phone-duplicate-${index}`) && (
                                    <p className="text-xs text-red-500">
                                      This phone number is already added
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="mt-2 flex w-full justify-end">
                              <button
                                type="button"
                                onClick={() => handleAddItem('phone')}
                                className={`flex items-center text-sm font-medium text-ink-muted hover:text-ink-secondary`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <path d="M12 5v14" />
                                  <path d="M5 12h14" />
                                </svg>
                                Add Phone
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Communication consent — per-channel-consent Rev 4
                          (D20-revised): TWO per-channel toggles (Email / SMS),
                          NO master switch. Each toggle is ON iff that channel's
                          enum === 'SUBSCRIBED'; toggling writes the channel's
                          enum on the contactsDB record and the marketing drain
                          syncs it to PocketFlows. */}
                        <div className="pt-2">
                          <ContactEditorSubheading
                            workspace={presentation === 'workspace'}
                            title="Communication consent"
                          >
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-ink-muted hover:text-ink-secondary" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <p>
                                    Controls which channels this contact can receive marketing
                                    broadcasts and automations on. A channel that's off is
                                    unsubscribed from that channel's communications.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </ContactEditorSubheading>
                          <div className="mb-4 mt-4 space-y-3">
                            <div className="flex items-start gap-2 pl-1">
                              <Switch
                                id="email-consent"
                                className="mt-1"
                                checked={formData.emailConsent === 'SUBSCRIBED'}
                                onCheckedChange={(v: boolean) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    emailConsent: v ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
                                  }))
                                }
                              />
                              <div>
                                <Label
                                  htmlFor="email-consent"
                                  className="block font-inter text-sm font-medium leading-5 text-ink"
                                >
                                  Email updates
                                </Label>
                                <p className="text-xs text-ink-muted">
                                  Allow this contact to receive email updates.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 pl-1">
                              {/* PE-1963: the switch stays ENABLED while locked.
                                A disabled Radix switch swallows pointer events
                                entirely, so it could never open the attestation
                                dialog and the admin-verified-START override would
                                have no entry point at all. Locking is enforced in
                                onCheckedChange instead: turning it ON opens the
                                confirm dialog and returns WITHOUT touching state
                                (so the switch does not optimistically flip before
                                the admin confirms), and turning it OFF is a no-op
                                because the contact is already unsubscribed. */}
                              <Switch
                                id="sms-consent"
                                className="mt-1"
                                checked={formData.smsConsent === 'SUBSCRIBED'}
                                onCheckedChange={(v: boolean) => {
                                  if (smsCarrierLocked) {
                                    if (v) setAdminVerifiedStartDialogOpen(true);
                                    return;
                                  }
                                  setFormData((prev) => ({
                                    ...prev,
                                    smsConsent: v ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
                                  }));
                                }}
                              />
                              <div>
                                <Label
                                  htmlFor="sms-consent"
                                  className="block font-inter text-sm font-medium leading-5 text-ink"
                                >
                                  Text messages
                                </Label>
                                {/* PE-1963: one plain sentence replaces the badge,
                                  the hover-gated info tooltip, and the standing
                                  "Donor confirmed they texted START" button. The
                                  reason a lock exists is not discoverable on hover,
                                  and the old button read as stray body text since
                                  it had no visual containment. */}
                                <p className="text-xs text-ink-muted">
                                  {smsCarrierLocked
                                    ? 'Turned off by donor texting STOP. Only the donor can turn it back on, by texting START.'
                                    : 'Allow this contact to receive text messages.'}
                                </p>
                              </div>
                            </div>
                            {/* Option B (communication-consent Finding 2): when the
                              contact has a NARROW per-broadcast PocketFlows opt-out
                              (a specific campaign/trigger), re-enabling a channel
                              here does NOT lift that per-broadcast unsubscribe — it
                              is preserved by design (marketing planChannelConsent
                              only lifts full/sms scopes). Surface that so the admin
                              isn't surprised the donor still won't get one broadcast.
                              Independent condition from smsCarrierLocked above --
                              a contact can have a narrow per-broadcast opt-out AND/OR
                              a full carrier-STOP lock; render both if both apply. */}
                            {contact?.hasBroadcastOptOut && (
                              <div className="flex items-start gap-2 rounded-md border-edge bg-paper-inset p-2">
                                <InformationCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-muted" />
                                <p className="text-xs text-ink-muted">
                                  Enabling a channel turns that channel on but keeps any
                                  specifically unsubscribed broadcasts off.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </ContactEditorSection>

                      {/* Address Tab */}
                      <ContactEditorSection
                        workspace={presentation === 'workspace'}
                        title="Address"
                        description="Manage the contact's saved addresses."
                        icon={MapPinIcon}
                        className={
                          presentation === 'workspace'
                            ? 'xl:col-start-1'
                            : activeTab === 'address'
                              ? 'space-y-4'
                              : 'hidden'
                        }
                      >
                        {/* Address Empty State or Address List */}
                        {activeAddresses.length === 0 ? (
                          <EmptyState
                            icon={<MapPinIcon className="h-6 w-6 text-ink-muted" />}
                            title="No addresses added"
                            description="Add an address to know where this contact is located."
                            actionLabel="Add Address"
                            onAction={() => handleAddItem('address')}
                          />
                        ) : (
                          <div className="w-full space-y-3">
                            {formData.addresses.map((address, index) => (
                              <div
                                key={index}
                                className={`space-y-2 rounded-md border-edge bg-paper-inset p-2 ${
                                  isEditMode && address.deleted
                                    ? 'border-red-200 bg-[#F5EDEB] opacity-50'
                                    : isEditMode && address.new
                                      ? 'border-accent/30 bg-accent/10'
                                      : ''
                                }`}
                              >
                                <div className="flex w-full items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-ink-secondary">
                                      Address {index + 1}
                                    </span>
                                    {isEditMode && address.deleted && (
                                      <span className="text-xs font-normal text-red-500">
                                        (Deleted)
                                      </span>
                                    )}
                                    {isEditMode && address.new && !address.deleted && (
                                      <span className="text-xs font-normal text-accent">(New)</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {formData.addresses.length > 1 && !address.deleted && (
                                      <div className="flex w-full items-center gap-2">
                                        <button
                                          type="button"
                                          className="flex items-center gap-1"
                                          onClick={() => {
                                            setFormData((prev) => ({
                                              ...prev,
                                              addresses: prev.addresses.map((addr, i) => ({
                                                ...addr,
                                                is_primary: i === index,
                                              })),
                                            }));
                                          }}
                                        >
                                          <span
                                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                                              address.is_primary ? 'border-sage' : 'border-edge'
                                            }`}
                                            aria-hidden
                                          >
                                            {address.is_primary && (
                                              <span className="h-2 w-2 rounded-full bg-sage" />
                                            )}
                                          </span>
                                          <span className="text-xs text-ink-muted">Primary</span>
                                        </button>
                                      </div>
                                    )}
                                    {!address.deleted && (
                                      <Button
                                        type="button"
                                        variant="dropdown"
                                        size="sm"
                                        onClick={() => {
                                          setFormData((prev) => {
                                            // In create mode, always fully remove items
                                            if (!isEditMode) {
                                              const newAddresses = prev.addresses.filter(
                                                (_, i) => i !== index,
                                              );
                                              // If we're removing the primary address, make the first remaining address primary
                                              if (address.is_primary && newAddresses.length > 0) {
                                                newAddresses[0].is_primary = true;
                                              }
                                              return {
                                                ...prev,
                                                addresses: newAddresses,
                                              };
                                            }

                                            // In edit mode, handle new vs existing items
                                            if (address.new) {
                                              const newAddresses = prev.addresses.filter(
                                                (_, i) => i !== index,
                                              );
                                              // If we're removing the primary address and it was new, make the first remaining address primary
                                              if (address.is_primary && newAddresses.length > 0) {
                                                newAddresses[0].is_primary = true;
                                              }
                                              return {
                                                ...prev,
                                                addresses: newAddresses,
                                              };
                                            }

                                            // Otherwise, soft-delete existing items in edit mode
                                            const updatedAddresses = prev.addresses.map(
                                              (addr, i) => {
                                                if (i === index) {
                                                  return {
                                                    ...addr,
                                                    deleted: true,
                                                    is_primary: false,
                                                  };
                                                }
                                                return addr;
                                              },
                                            );

                                            // If we're soft-deleting the primary address, make the first remaining active address primary
                                            if (address.is_primary) {
                                              const activeAddresses = updatedAddresses.filter(
                                                (addr) => !addr.deleted,
                                              );
                                              if (activeAddresses.length > 0) {
                                                activeAddresses[0].is_primary = true;
                                              }
                                            }

                                            return {
                                              ...prev,
                                              addresses: updatedAddresses,
                                            };
                                          });
                                        }}
                                        className={`h-6 rounded-full p-0 text-xs hover:bg-paper-inset ${
                                          formData.addresses.length > 1 ? 'w-8' : 'w-6'
                                        }`}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M18 6L6 18" />
                                          <path d="M6 6l12 12" />
                                        </svg>
                                      </Button>
                                    )}
                                    {isEditMode && address.deleted && (
                                      <Button
                                        type="button"
                                        variant="dropdown"
                                        size="sm"
                                        onClick={() => {
                                          setFormData((prev) => {
                                            const updatedAddresses = prev.addresses.map(
                                              (addr, i) => {
                                                if (i === index) {
                                                  // Remove the deleted flag
                                                  const { deleted, ...rest } = addr;
                                                  return rest;
                                                }
                                                return addr;
                                              },
                                            );

                                            // If there's no primary address after restoration, make this one primary
                                            const hasPrimary = updatedAddresses.some(
                                              (addr) => addr.is_primary,
                                            );
                                            if (!hasPrimary) {
                                              updatedAddresses[index].is_primary = true;
                                            }

                                            return {
                                              ...prev,
                                              addresses: updatedAddresses,
                                            };
                                          });
                                        }}
                                        className={`h-6 rounded-full bg-sage-light p-0 text-xs hover:bg-[#E2EBE2] ${
                                          formData.addresses.length > 1 ? 'w-8' : 'w-6'
                                        }`}
                                        title="Restore address"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                          <path d="M21 3v5h-5" />
                                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                          <path d="M3 21v-5h5" />
                                        </svg>
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                {/* @ts-expect-error - Type incompatibility with React 18 ForwardRefExoticComponent */}
                                <AddressAutofill
                                  accessToken={import.meta.env.VITE_APP_SCOPED_MAPBOX_TOKEN}
                                  onRetrieve={(e) => {
                                    handleAddressChange(e, index);
                                  }}
                                >
                                  <div className="space-y-3">
                                    {/* Address Line 1 */}
                                    <div>
                                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                        Address Line 1
                                      </Label>
                                      <Input
                                        type="text"
                                        name={`address-${index}-line1`}
                                        value={address.line1}
                                        onChange={(e) =>
                                          handleInputChange(
                                            'address',
                                            index,
                                            'line1',
                                            e.target.value,
                                          )
                                        }
                                        onFocus={() => {
                                          // Address duplicate errors are handled by real-time validation
                                        }}
                                        className={`h-7 border bg-white ${
                                          isFieldInvalid(`address-duplicate-${index}`)
                                            ? 'border-red-500'
                                            : 'border-edge'
                                        }`}
                                        placeholder="Enter street address"
                                        autoComplete="address-line1"
                                        disabled={address.deleted}
                                      />
                                    </div>

                                    {/* Address Line 2 */}
                                    <div>
                                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                        Address Line 2
                                      </Label>
                                      <Input
                                        type="text"
                                        name={`address-${index}-line2`}
                                        value={address.line2}
                                        onChange={(e) =>
                                          handleInputChange(
                                            'address',
                                            index,
                                            'line2',
                                            e.target.value,
                                          )
                                        }
                                        onFocus={() => {
                                          // Address duplicate errors are handled by real-time validation
                                        }}
                                        className={`h-7 border bg-white ${
                                          isFieldInvalid(`address-duplicate-${index}`)
                                            ? 'border-red-500'
                                            : 'border-edge'
                                        }`}
                                        placeholder="Apartment, suite, etc. (optional)"
                                        autoComplete="address-line2"
                                        disabled={address.deleted}
                                      />
                                    </div>

                                    <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-3">
                                      {/* City */}
                                      <div>
                                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                          City
                                        </Label>
                                        <Input
                                          type="text"
                                          name={`address-${index}-city`}
                                          value={address.city}
                                          onChange={(e) =>
                                            handleInputChange(
                                              'address',
                                              index,
                                              'city',
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() => {
                                            // Address duplicate errors are handled by real-time validation
                                          }}
                                          className={`h-7 border bg-white ${
                                            isFieldInvalid(`address-duplicate-${index}`)
                                              ? 'border-red-500'
                                              : 'border-edge'
                                          }`}
                                          placeholder="Enter city"
                                          autoComplete="address-level2"
                                          disabled={address.deleted}
                                        />
                                      </div>

                                      {/* State */}
                                      <div>
                                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                          State
                                        </Label>
                                        <Input
                                          type="text"
                                          name={`address-${index}-region`}
                                          value={address.region}
                                          onChange={(e) =>
                                            handleInputChange(
                                              'address',
                                              index,
                                              'region',
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() => {
                                            // Address duplicate errors are handled by real-time validation
                                          }}
                                          className={`h-7 border bg-white ${
                                            isFieldInvalid(`address-duplicate-${index}`)
                                              ? 'border-red-500'
                                              : 'border-edge'
                                          }`}
                                          placeholder="Enter state"
                                          autoComplete="address-level1"
                                          disabled={address.deleted}
                                        />
                                      </div>
                                      {/* ZIP/Postal Code */}
                                      <div className="col-span-1">
                                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                          ZIP/Postal Code
                                        </Label>
                                        <Input
                                          type="text"
                                          name={`address-${index}-postal`}
                                          value={address.postal}
                                          onChange={(e) =>
                                            handleInputChange(
                                              'address',
                                              index,
                                              'postal',
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() => {
                                            // Address duplicate errors are handled by real-time validation
                                          }}
                                          className={`h-7 border bg-white ${
                                            isFieldInvalid(`address-duplicate-${index}`)
                                              ? 'border-red-500'
                                              : 'border-edge'
                                          }`}
                                          placeholder="Enter ZIP code"
                                          autoComplete="postal-code"
                                          disabled={address.deleted}
                                        />
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1">
                                      {/* Country */}
                                      <div>
                                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                          Country
                                        </Label>
                                        <FormSelect
                                          name={`address-${index}-country`}
                                          value={address.country}
                                          onChange={(e) =>
                                            handleInputChange(
                                              'address',
                                              index,
                                              'country',
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() => {
                                            // Address duplicate errors are handled by real-time validation
                                          }}
                                          className={`block h-7 focus:ring-sage ${
                                            isFieldInvalid(`address-duplicate-${index}`)
                                              ? 'border-red-500'
                                              : ''
                                          }`}
                                          autoComplete="country"
                                          disabled={address.deleted}
                                        >
                                          <option value="">Select a country</option>
                                          {address.country &&
                                            !countryLabels.has(address.country) && (
                                              <option value={address.country}>
                                                {address.country}
                                              </option>
                                            )}
                                          {countries.map((country: any) => (
                                            <option key={country.value} value={country.label}>
                                              {country.label}
                                            </option>
                                          ))}
                                        </FormSelect>
                                      </div>
                                    </div>
                                  </div>
                                </AddressAutofill>
                                {isFieldInvalid(`address-duplicate-${index}`) && (
                                  <p className="mt-1 text-xs text-red-500">
                                    This address is already added
                                  </p>
                                )}
                              </div>
                            ))}
                            <div className="mt-2 flex w-full justify-end">
                              <button
                                type="button"
                                onClick={() => handleAddItem('address')}
                                className={`flex items-center text-sm font-medium text-ink-muted hover:text-ink-secondary`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1"
                                >
                                  <path d="M12 5v14" />
                                  <path d="M5 12h14" />
                                </svg>
                                Add Address
                              </button>
                            </div>
                          </div>
                        )}
                      </ContactEditorSection>

                      {/* Settings Tab — per-contact Contact Portal toggles. */}
                      <ContactEditorSection
                        workspace={presentation === 'workspace'}
                        title="Contact portal"
                        drawerTitle="Contact Portal"
                        description="Control what this donor can update and access."
                        icon={IdentificationIcon}
                        className={
                          presentation === 'workspace'
                            ? 'xl:col-start-2 xl:row-start-1'
                            : activeTab === 'settings'
                              ? 'space-y-4'
                              : 'hidden'
                        }
                      >
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 pl-1">
                            <Switch
                              id="allow-profile-edit"
                              checked={formData.allowProfileEdit !== false}
                              onCheckedChange={(v: boolean) =>
                                setFormData((prev) => ({ ...prev, allowProfileEdit: v }))
                              }
                            />
                            <div>
                              <div className="flex items-center gap-1">
                                <Label
                                  htmlFor="allow-profile-edit"
                                  className="block font-inter text-sm font-medium leading-5 text-ink"
                                >
                                  Allow this donor to edit their profile
                                </Label>
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-ink-muted hover:text-ink-secondary" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p>
                                        When enabled, this donor can edit their name, phone,
                                        address, and profile photo from the User Login widget. When
                                        disabled, they can still view their profile but cannot make
                                        changes.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 pl-1">
                            <Switch
                              id="show-tax-documents"
                              checked={formData.showTaxDocuments !== false}
                              onCheckedChange={(v: boolean) =>
                                setFormData((prev) => ({ ...prev, showTaxDocuments: v }))
                              }
                            />
                            <div>
                              <div className="flex items-center gap-1">
                                <Label
                                  htmlFor="show-tax-documents"
                                  className="block font-inter text-sm font-medium leading-5 text-ink"
                                >
                                  Show tax documents to this donor
                                </Label>
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-ink-muted hover:text-ink-secondary" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p>
                                        When enabled, this donor can access their year-end tax
                                        documents in the User Login widget. When disabled, the Tax
                                        Documents tab is hidden entirely for them.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          </div>
                        </div>
                      </ContactEditorSection>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <AdminVerifiedStartConfirmDialog
        open={adminVerifiedStartDialogOpen}
        onOpenChange={setAdminVerifiedStartDialogOpen}
        onConfirm={handleConfirmAdminVerifiedStart}
        pending={adminVerifiedStartPending}
      />
    </>
  );
}
